import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import { MessageCircle, ClipboardList, Crown, FilePenLine, Inbox, Calendar, Clipboard, SquareCheckBig, User, Banknote, Folder } from "lucide-react";
import ChatWidget from '../../common/ChatWidget/ChatWidget';
import { estadosDisponibles } from '@/config/constants';

const getStatusInfo = (status) => {
  return estadosDisponibles.find(e => e.id === status) ||
    { icon: '❓', color: 'gray', name: status };
}

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tramiteRequests, setTramiteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatVisible, setChatVisible] = useState(false);
  const tramiteRequestId = tramiteRequests.length > 0 ? tramiteRequests[0]._id : null;
  const tramiteType = tramiteRequests.length > 0 ? tramiteRequests[0].tramite_type : null;

  const chatMessage = `Hola, me comunico con el soporte de Mistramites.mx `;
  const whatsappUrl = `https://wa.me/525585353729?text=${encodeURIComponent(chatMessage)}`;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleOpenSolicitud = (solicitud) => {
    setTimeout(() => {
      navigate(`/applications/${solicitud._id}`);
    }, 500);
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Obtener estadísticas (común para todos)
      const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/dashboard/stats`, {
        headers
      });
      const statsData = statsResponse.ok ? await statsResponse.json() : null;
      setStats(statsData);

      // Obtener solicitudes según el rol
      let requestsData = [];

      if (user?.role === 'admin' || user?.role === 'superadmin') {
        // Admin ve las solicitudes recientes del sistema (todas)
        const adminResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applicatitons/enriched`, {
          headers
        });
        if (adminResponse.ok) {
          const adminData = await adminResponse.json();
          requestsData = (adminData.solicitudes || []).slice(0, 5); // Últimas 5 del sistema
        }
      } else if (user?.role === 'gestor') {
        // Gestor ve las solicitudes asignadas a él
        const gestorResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/managers/assigned-requests`, {
          headers
        });
        if (gestorResponse.ok) {
          requestsData = await gestorResponse.json();
        }
      } else {
        // Cliente ve sus propias solicitudes
        const clientResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applications/my-requests`, {
          headers
        });
        if (clientResponse.ok) {
          requestsData = await clientResponse.json();
        }
      }

      setTramiteRequests(requestsData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Cargando dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            ¡Bienvenido, {user?.name}!
          </h1>
          <p className="text-gray-600 mt-2">
            Gestiona todos tus trámites desde un solo lugar
          </p>
        </div>

        {/* Quick Actions con Chat - ARRIBA */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ActionCard
            title={(user?.role === 'admin' || user?.role === 'superadmin')
              ? 'Todas las Solicitudes'
              : user?.role === 'gestor'
                ? 'Solicitudes Asignadas'
                : 'Mis Solicitudes'
            }
            description={(user?.role === 'admin' || user?.role === 'superadmin')
              ? 'Supervisar todas las solicitudes del sistema'
              : user?.role === 'gestor'
                ? 'Ver solicitudes asignadas a mí'
                : 'Ver todas mis solicitudes'
            }
            icon={<ClipboardList />}
            color="blue"
            onClick={() => {
              navigate('/applications', { replace: true });
            }}
          />
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <ActionCard
              title="Panel Admin"
              description="Gestionar categorías y trámites"
              icon={<Crown />}
              color="purple"
              onClick={() => navigate('/admin')}
            />
          )}
          {(user?.role === 'gestor' || user?.role === 'admin' || user?.role === 'superadmin') && (
            <ActionCard
              title="Panel Gestor"
              description="Revisar documentos y casos"
              icon={<ClipboardList />}
              color="blue"
              onClick={() => navigate('/gestor')}
            />
          )}
          {(user?.role === 'gestor' || user?.role === 'cliente') && (
            <ActionCard
              title="Soporte"
              description="Chat en tiempo real"
              icon={<MessageCircle />}
              color="purple"
              onClick={() => window.open(whatsappUrl, '_blank')}
            />
          )}
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Trámites Activos"
              value={stats.active_tramites || 0}
              icon={<Clipboard />}
              color="blue"
            />
            <StatCard
              title="Completados"
              value={stats.completed_tramites || 0}
              icon={<SquareCheckBig />}
              color="green"
            />
            <StatCard
              title="En Proceso"
              value={stats.in_progress_tramites || 0}
              icon={<Hourglass />}
              color="yellow"
            />
            <StatCard
              title="Total Gastado"
              value={`$${stats.total_spent || 0}`}
              icon={<Banknote />}
              color="purple"
            />
          </div>
        )}

        {/* Recent Requests - Con título y formato dinámico según rol */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {(user?.role === 'admin' || user?.role === 'superadmin')
              ? 'Solicitudes Recientes del Sistema'
              : user?.role === 'gestor'
                ? 'Solicitudes asignadas a mí'
                : 'Mis Solicitudes Recientes'
            }
          </h2>

          {tramiteRequests.length > 0 ? (
            (user?.role === 'admin' || user?.role === 'superadmin') ? (
              /* Tabla para admin - formato compacto */
              <AdminSolicitudesTable solicitudes={tramiteRequests} />
            ) : (
              /* Cards para cliente/gestor - formato detallado */
              <div className="space-y-4">
                {tramiteRequests.map((request) => (
                  <RequestCard key={request._id} request={request} />
                ))}
              </div>
            )
          ) : (
            <EmptyState userRole={user?.role} />
          )}
        </div>

        <div style={{ position: "fixed", right: 5, bottom: 20, maxWidth: 400 }}>
          <ChatWidget
            tramiteRequestId={0}
            isVisible={chatVisible}
            onToggleVisibility={() => { }}
            tramiteName={tramiteType?.name}
          />
        </div>

        {/* Mis Trámites Activos con Chat */}
        {false && tramiteRequests.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {(user?.role === 'admin' || user?.role === 'superadmin')
                ? 'Supervisión de Chats del Sistema'
                : user?.role === 'gestor'
                  ? 'Chats de Mis Casos Asignados'
                  : 'Mis Trámites con Chat Activo'
              }
            </h2>
            <div className="grid gap-4">
              {tramiteRequests.slice(0, 3).map((request) => (
                <div key={request._id} className="flex items-center justify-between p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 border border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className="text-3xl">
                      <FilePenLine style={{ scale: 3 }} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">
                        {request.tramite_name || request.tramite_nombre || 'Trámite'}
                      </h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <Folder /> {request.categoria_name || request.categoria_nombre || 'Categoría'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Estado: <span className="font-medium text-gray-700">{request.status}</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        ID: {request._id ? request._id.substring(0, 8) : 'N/A'}...
                      </p>
                      {/* Mostrar información del cliente si es admin/gestor */}
                      {(user?.role === 'admin' || user?.role === 'gestor' || user?.role === 'superadmin') && (
                        <p className="text-xs text-blue-600 mt-1">
                          👤 Cliente: {request.cliente_nombre || 'Cliente'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => handleOpenSolicitud(request)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors text-sm font-medium"
                    >
                      Ver Detalles
                    </button>
                    <button
                      onClick={() => window.location.href = `/application/${request._id}`}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors text-sm font-medium"
                    >
                      {(user?.role === 'admin' || user?.role === 'superadmin') ? 'Supervisar Chat' : 'Abrir Chat'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Tarjeta de estadística
 */
const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200'
  };

  return (
    <div className={`p-6 rounded-2xl border-2 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  );
};

/**
 * Tarjeta de solicitud mejorada con información clara
 */
const RequestCard = ({ request }) => {
  const navigate = useNavigate();

  const handleOpenSolicitud = (solicitud) => {
    setTimeout(() => {
      navigate(`/applications/${solicitud._id}`);
    }, 500);
  };

  const estadoInfo = getStatusInfo(request.status);

  // Información del trámite más clara
  const tramiteNombre = request.tramite_name || request.tramite_nombre || 'Trámite';
  const categoriaNombre = request.categoria_name || request.categoria_nombre || 'Categoría';
  const tramiteId = request._id ? request._id.substring(0, 8) : 'N/A';

  return (
    <div className="flex items-center justify-between p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300 border border-gray-200 hover:border-gray-300">
      <div className="flex items-start space-x-4">
        <div className="text-3xl">{estadoInfo.icon}</div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h4 className="font-bold text-gray-900 text-lg">{tramiteNombre}</h4>
            <span className="text-sm text-gray-500">#{tramiteId}</span>
          </div>

          <p className="text-sm text-gray-600 mb-2 flex row items-center gap-1">
            <Folder /> Categoría: <span className="font-medium">{categoriaNombre}</span>
          </p>

          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs flex row items-center gap-1   font-medium bg-${estadoInfo.color}-100 text-${estadoInfo.color}-800`}>
              { estadoInfo.icon } {estadoInfo.name}
            </span>
            <span className="text-xs text-gray-500 flex row items-center gap-1">
              <Calendar />
              {new Date(request.created_at).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </span>
          </div>

          {/* Información adicional si está disponible */}
          {request.gestor_nombre && (
            <p className="text-xs text-gray-500 mt-1">
              👨‍💼 Gestor: {request.gestor_nombre}
            </p>
          )}
        </div>
      </div>

      <div className="text-right">
        <button
          onClick={() => handleOpenSolicitud(request)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors text-sm font-medium"
        >
          Ver Detalles
        </button>
        <div className="text-xs text-gray-400">
          ID: {tramiteId}...
        </div>
      </div>
    </div>
  );
};

/**
 * Estado vacío inteligente según rol
 */
const EmptyState = ({ userRole }) => {
  const content = {
    admin: {
      icon: '🏢',
      title: 'No hay solicitudes en el sistema',
      description: 'Los clientes aún no han enviado solicitudes de trámites',
      buttonText: 'Ver Panel Admin',
      buttonAction: () => window.location.href = '/admin'
    },
    gestor: {
      icon: '👨‍💼',
      title: 'No tienes casos asignados',
      description: 'El admin aún no te ha asignado solicitudes para gestionar',
      buttonText: 'Ver Panel Gestor',
      buttonAction: () => window.location.href = '/gestor'
    },
    cliente: {
      icon: '📋',
      title: 'No tienes solicitudes aún',
      description: 'Comienza explorando nuestras categorías de trámites',
      buttonText: 'Explorar Trámites',
      buttonAction: () => window.location.href = '/categorias'
    }
  };

  const config = content[userRole] || content.cliente;

  return (
    <div className="text-center py-12">
      <div className="text-6xl mb-4 flex row justify-center">{config.icon}</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {config.title}
      </h3>
      <p className="text-gray-500 mb-6">
        {config.description}
      </p>
      <button
        onClick={config.buttonAction}
        className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
      >
        {config.buttonText}
      </button>
    </div>
  );
};

/**
 * Tarjeta de acción rápida
 */
const ActionCard = ({ title, description, icon, color, onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    purple: 'bg-purple-600 hover:bg-purple-700'
  };

  return (
    <div
      className={`${colorClasses[color]} text-white p-6 rounded-2xl cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg`}
      onClick={onClick}
    >
      <div className="text-3xl mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-200">{description}</p>
    </div>
  );
};

const AdminSolicitudesTable = ({ solicitudes }) => {

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cliente</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Trámite</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Fecha Solicitud</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Estado</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Gestor</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Acciones</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {solicitudes.map((request) => {
            const estadoInfo = getStatusInfo(request.status);
            const tramiteNombre = request.tramite_name || request.tramite_nombre || 'Trámite eliminado';
            const clienteNombre = request.cliente_name || request.cliente_nombre || 'Cliente';
            const gestorNombre = request.gestor_name || request.gestor_nombre || 'Sin Asignar';
            const tramiteId = request._id ? request._id.substring(0, 8) : 'N/A';

            return (
              <tr key={request._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="text-lg mr-2 flex row items-center gap-2"><User /></div>
                    <div>
                      <div className="font-medium text-gray-900">{clienteNombre}</div>
                      <div className="text-xs text-gray-500">#{tramiteId}</div>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div>
                    <div className="font-medium text-gray-900">{tramiteNombre}</div>
                    <div className="text-sm text-gray-500 flex row items-center gap-2">
                      <Folder /> {request.categoria_name || request.categoria_nombre || 'Sin categoría'}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div className="text-sm text-gray-900">
                    {new Date(request.created_at).toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                </td>

                <td className="px-4 py-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${estadoInfo.bg} ${estadoInfo.color}`}>
                    {estadoInfo.icon} {estadoInfo.text}
                  </span>
                </td>

                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="text-sm text-gray-900 font-medium">
                      {gestorNombre}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => window.location.href = `/applications/${request._id}`}
                      className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium flex row items-center gap-1"
                      title="Ver solicitud completa"
                    >
                      Ver
                    </button>
                    <button
                      onClick={() => window.location.href = `/applications/${request._id}/documents`}
                      className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-xs font-medium flex row items-center gap-1"
                      title="Supervisar chat"
                    >
                      <MessageCircle /> Chat
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Dashboard;